<template>
  <div class="merchant-page" v-if="!loading">
    <!-- 无权限提示 -->
    <div v-if="!admin" class="denied">
      <h2>商户后台管理</h2>
      <p>当前登录账号（{{ mobile || '未知' }}）不是平台管理员，无权限访问本模块。</p>
      <p class="tip">如确需访问，请联系系统管理员将您的手机号加入平台管理员白名单。</p>
    </div>

    <div v-else>
      <!-- 概览 -->
      <div class="cards">
        <div class="card"><div class="num">{{ overview.customerTotal || 0 }}</div><div class="lbl">客户总数</div></div>
        <div class="card"><div class="num">{{ overview.accountSetsTotal || 0 }}</div><div class="lbl">账套总数</div></div>
        <div class="card warn"><div class="num">{{ overview.expiring || 0 }}</div><div class="lbl">30天内到期</div></div>
        <div class="card danger"><div class="num">{{ overview.expired || 0 }}</div><div class="lbl">已过期</div></div>
        <div class="card danger"><div class="num">{{ overview.stopped || 0 }}</div><div class="lbl">已停用</div></div>
      </div>

      <!-- 子导航 -->
      <div class="tabs">
        <span :class="{active: tab==='customer'}" @click="tab='customer'">客户账户</span>
        <span :class="{active: tab==='grant'}" @click="switchTab('grant')">开通记录</span>
        <span :class="{active: tab==='package'}" @click="switchTab('package')">套餐维护</span>
        <span :class="{active: tab==='admin'}" @click="switchTab('admin')">平台管理员</span>
      </div>

      <!-- 客户账户 -->
      <div v-show="tab==='customer'" class="panel">
        <div class="bar">
          <Button color="primary" @click="openCreate">新建客户</Button>
          <input class="search" type="text" v-model="keyword" placeholder="手机号/客户名/联系人" @keyup.enter="loadCustomers" />
          <select v-model="statusFilter" @change="loadCustomers" style="height:30px">
            <option value="">全部状态</option>
            <option value="正常">正常</option>
            <option value="停用">停用</option>
          </select>
          <Button @click="loadCustomers">查询</Button>
        </div>
        <Table :datas="customers" stripe>
          <TableItem title="客户名称" prop="customerName"></TableItem>
          <TableItem title="联系人" prop="realName"></TableItem>
          <TableItem title="手机号" prop="mobile"></TableItem>
          <TableItem title="套餐" prop="packageName"></TableItem>
          <TableItem title="账套" :width="90">
            <template slot-scope="{ data }">{{ data.usedAccountSets }}/{{ data.accountSetsNum }}</template>
          </TableItem>
          <TableItem title="状态" :width="70">
            <template slot-scope="{ data }">
              <span :class="data.merchantStatus==='停用' ? 'st-stop' : 'st-ok'">{{ data.merchantStatus }}</span>
            </template>
          </TableItem>
          <TableItem title="有效期" prop="expirationDate"></TableItem>
          <TableItem title="过期" :width="60">
            <template slot-scope="{ data }">
              <span :class="data.expired ? 'st-stop' : 'st-ok'">{{ data.expired ? '是' : '否' }}</span>
            </template>
          </TableItem>
          <TableItem title="操作" :width="320">
            <template slot-scope="{ data }">
              <Button text-color="primary" size="xs" @click="openAccountSet(data)">开账套</Button>
              <Button text-color="primary" size="xs" @click="openGrant(data)">开通套餐</Button>
              <Button text-color="primary" size="xs" @click="resetPwd(data)">重置密码</Button>
              <Button text-color="primary" size="xs" @click="toggleStatus(data)">{{ data.merchantStatus==='停用' ? '启用' : '停用' }}</Button>
              <Button text-color="primary" size="xs" @click="openEdit(data)">编辑</Button>
              <Button text-color="red" size="xs" @click="delCustomer(data)">删除</Button>
            </template>
          </TableItem>
        </Table>
        <Pagination v-model="custPage" @change="loadCustomers" layout="pager" small align="center" v-if="customers.length" />
        <p v-if="!customers.length" class="empty">暂无客户</p>
      </div>

      <!-- 开通记录 -->
      <div v-show="tab==='grant'" class="panel">
        <Table :datas="grants" stripe>
          <TableItem title="客户" prop="customerName"></TableItem>
          <TableItem title="手机号" prop="mobile"></TableItem>
          <TableItem title="套餐" prop="packageName"></TableItem>
          <TableItem title="类型" prop="grantType"></TableItem>
          <TableItem title="金额" prop="amount"></TableItem>
          <TableItem title="月数" prop="months"></TableItem>
          <TableItem title="开始" prop="startDate"></TableItem>
          <TableItem title="结束" prop="endDate"></TableItem>
          <TableItem title="操作人" prop="operator"></TableItem>
          <TableItem title="备注" prop="remark"></TableItem>
        </Table>
        <Pagination v-model="grantPage" @change="loadGrants" layout="pager" small align="center" v-if="grants.length" />
        <p v-if="!grants.length" class="empty">暂无开通记录</p>
      </div>

      <!-- 套餐维护 -->
      <div v-show="tab==='package'" class="panel">
        <div class="bar">
          <Button color="primary" @click="openPackage(null)">新建套餐</Button>
        </div>
        <Table :datas="packages" stripe>
          <TableItem title="套餐名" prop="name"></TableItem>
          <TableItem title="价格" prop="price"></TableItem>
          <TableItem title="时长(月)" prop="months"></TableItem>
          <TableItem title="账套数" prop="accountSetsNum"></TableItem>
          <TableItem title="核算数" prop="accountingNum"></TableItem>
          <TableItem title="状态" prop="status"></TableItem>
          <TableItem title="操作" :width="140">
            <template slot-scope="{ data }">
              <Button text-color="primary" size="xs" @click="openPackage(data)">编辑</Button>
              <Button text-color="red" size="xs" @click="delPackage(data)">删除</Button>
            </template>
          </TableItem>
        </Table>
      </div>

      <!-- 平台管理员 -->
      <div v-show="tab==='admin'" class="panel">
        <div class="bar">
          <Button color="primary" @click="adminForm={mobile:'',remark:''};showAdmin=true">添加管理员</Button>
        </div>
        <Table :datas="admins" stripe>
          <TableItem title="手机号" prop="mobile"></TableItem>
          <TableItem title="备注" prop="remark"></TableItem>
          <TableItem title="创建时间" prop="createTime"></TableItem>
          <TableItem title="操作" :width="80">
            <template slot-scope="{ data }">
              <Button text-color="red" size="xs" @click="delAdmin(data)">删除</Button>
            </template>
          </TableItem>
        </Table>
      </div>
    </div>

    <!-- 新建/编辑客户 -->
    <div class="mask" v-if="showCustomer" @click.self="showCustomer=false">
      <div class="dialog">
        <div class="dialog-head">{{ customerForm.id ? '编辑客户' : '新建客户' }}
          <span class="x" @click="showCustomer=false">×</span></div>
        <div class="dialog-body">
          <div class="frow"><label>手机号</label><input :disabled="!!customerForm.id" v-model="customerForm.mobile" placeholder="登录账号/手机号" /></div>
          <div class="frow"><label>客户名称</label><input v-model="customerForm.customerName" placeholder="企业/客户名称" /></div>
          <div class="frow"><label>联系人</label><input v-model="customerForm.realName" placeholder="联系人姓名" /></div>
          <div class="frow"><label>邮箱</label><input v-model="customerForm.email" placeholder="选填" /></div>
          <div class="frow" v-if="!customerForm.id"><label>初始密码</label><input v-model="customerForm.initPassword" placeholder="留空随机6位" /></div>
          <div class="frow"><label>绑定套餐</label>
            <select v-model="customerForm.packageId">
              <option :value="null">不绑定</option>
              <option v-for="p in pkgOptions" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
          </div>
          <div class="frow"><label>账套数</label><input type="number" v-model="customerForm.accountSetsNum" /></div>
          <div class="frow"><label>核算数</label><input type="number" v-model="customerForm.accountingNum" /></div>
          <div class="frow" v-if="customerForm.id"><label>状态</label>
            <select v-model="customerForm.merchantStatus">
              <option value="正常">正常</option>
              <option value="停用">停用</option>
            </select>
          </div>
          <div class="frow"><label>有效期至</label><DatePicker type="date" v-model="customerForm.expirationDate"></DatePicker></div>
        </div>
        <div class="dialog-foot">
          <Button @click="showCustomer=false">取消</Button>
          <Button color="primary" @click="saveCustomer">保存</Button>
        </div>
      </div>
    </div>

    <!-- 开账套 -->
    <div class="mask" v-if="showAccountSet" @click.self="showAccountSet=false">
      <div class="dialog">
        <div class="dialog-head">为客户「{{ asCustomerName }}」开账套
          <span class="x" @click="showAccountSet=false">×</span></div>
        <div class="dialog-body">
          <div class="frow"><label>单位名称</label><input v-model="asForm.companyName" placeholder="必填" /></div>
          <div class="frow"><label>启用年月</label><DatePicker type="month" v-model="asForm.enableDate"></DatePicker></div>
          <div class="frow"><label>统一信用代码</label><input v-model="asForm.creditCode" /></div>
          <div class="frow"><label>地址</label><input v-model="asForm.address" /></div>
          <div class="frow"><label>会计准则</label>
            <select v-model="asForm.accountingStandards">
              <option :value="0">小企业准则</option>
              <option :value="1">企业准则</option>
            </select>
          </div>
          <div class="frow"><label>纳税人类型</label>
            <select v-model="asForm.vatType">
              <option :value="0">小规模</option>
              <option :value="1">一般纳税人</option>
            </select>
          </div>
        </div>
        <div class="dialog-foot">
          <Button @click="showAccountSet=false">取消</Button>
          <Button color="primary" @click="submitAccountSet">确认开启</Button>
        </div>
      </div>
    </div>

    <!-- 开通/续期/变更套餐 -->
    <div class="mask" v-if="showGrant" @click.self="showGrant=false">
      <div class="dialog">
        <div class="dialog-head">为客户「{{ grantCustomerName }}」开通/续期套餐
          <span class="x" @click="showGrant=false">×</span></div>
        <div class="dialog-body">
          <div class="frow"><label>套餐</label>
            <select v-model="grantForm.packageId">
              <option :value="null">自定义（不绑定套餐）</option>
              <option v-for="p in pkgOptions" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
          </div>
          <div class="frow"><label>类型</label>
            <select v-model="grantForm.grantType">
              <option value="开通">开通</option>
              <option value="续期">续期</option>
              <option value="变更">变更</option>
            </select>
          </div>
          <div class="frow"><label>金额</label><input v-model="grantForm.amount" placeholder="留空按套餐价格" /></div>
          <div class="frow"><label>时长(月)</label><input type="number" v-model="grantForm.months" placeholder="留空按套餐" /></div>
          <div class="frow"><label>开始日期</label><DatePicker type="date" v-model="grantForm.startDate"></DatePicker></div>
          <div class="frow"><label>备注</label><input v-model="grantForm.remark" /></div>
        </div>
        <div class="dialog-foot">
          <Button @click="showGrant=false">取消</Button>
          <Button color="primary" @click="submitGrant">确认</Button>
        </div>
      </div>
    </div>

    <!-- 套餐编辑 -->
    <div class="mask" v-if="showPackage" @click.self="showPackage=false">
      <div class="dialog">
        <div class="dialog-head">{{ pkgForm.id ? '编辑套餐' : '新建套餐' }}
          <span class="x" @click="showPackage=false">×</span></div>
        <div class="dialog-body">
          <div class="frow"><label>套餐名</label><input v-model="pkgForm.name" /></div>
          <div class="frow"><label>价格</label><input v-model="pkgForm.price" /></div>
          <div class="frow"><label>时长(月)</label><input type="number" v-model="pkgForm.months" /></div>
          <div class="frow"><label>账套数</label><input type="number" v-model="pkgForm.accountSetsNum" /></div>
          <div class="frow"><label>核算数</label><input type="number" v-model="pkgForm.accountingNum" /></div>
          <div class="frow"><label>出纳模块</label>
            <select v-model="pkgForm.cashierModule"><option :value="1">开</option><option :value="0">关</option></select>
          </div>
          <div class="frow"><label>固定资产</label>
            <select v-model="pkgForm.fixedAssetModule"><option :value="1">开</option><option :value="0">关</option></select>
          </div>
          <div class="frow"><label>发票模块</label>
            <select v-model="pkgForm.invoiceModule"><option :value="1">开</option><option :value="0">关</option></select>
          </div>
          <div class="frow"><label>工资模块</label>
            <select v-model="pkgForm.salaryModule"><option :value="1">开</option><option :value="0">关</option></select>
          </div>
          <div class="frow"><label>报表模块</label>
            <select v-model="pkgForm.reportModule"><option :value="1">开</option><option :value="0">关</option></select>
          </div>
          <div class="frow"><label>状态</label>
            <select v-model="pkgForm.status"><option value="启用">启用</option><option value="停用">停用</option></select>
          </div>
        </div>
        <div class="dialog-foot">
          <Button @click="showPackage=false">取消</Button>
          <Button color="primary" @click="savePackage">保存</Button>
        </div>
      </div>
    </div>

    <!-- 添加管理员 -->
    <div class="mask" v-if="showAdmin" @click.self="showAdmin=false">
      <div class="dialog">
        <div class="dialog-head">添加平台管理员
          <span class="x" @click="showAdmin=false">×</span></div>
        <div class="dialog-body">
          <div class="frow"><label>手机号</label><input v-model="adminForm.mobile" placeholder="可登录系统的手机号" /></div>
          <div class="frow"><label>备注</label><input v-model="adminForm.remark" /></div>
        </div>
        <div class="dialog-foot">
          <Button @click="showAdmin=false">取消</Button>
          <Button color="primary" @click="saveAdmin">保存</Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MerchantIndex',
  data() {
    return {
      loading: true,
      admin: false,
      mobile: '',
      tab: 'customer',
      overview: {},
      keyword: '',
      statusFilter: '',
      customers: [],
      custPage: { page: 1, size: 20, total: 0 },
      grants: [],
      grantPage: { page: 1, size: 20, total: 0 },
      packages: [],
      pkgOptions: [],
      admins: [],
      showCustomer: false,
      customerForm: {},
      showAccountSet: false,
      asCustomerId: null,
      asCustomerName: '',
      asForm: {},
      showGrant: false,
      grantCustomerId: null,
      grantCustomerName: '',
      grantForm: {},
      showPackage: false,
      pkgForm: {},
      showAdmin: false,
      adminForm: {},
    };
  },
  mounted() {
    this.$api.merchant.permission().then((res) => {
      this.admin = !!(res.data && res.data.admin);
      this.mobile = res.data ? res.data.mobile : '';
      this.loading = false;
      if (this.admin) {
        this.loadOverview();
        this.loadCustomers();
        this.loadPackages();
        this.loadAdmins();
      }
    }).catch(() => {
      this.loading = false;
      this.admin = false;
    });
  },
  methods: {
    switchTab(t) {
      this.tab = t;
      if (t === 'grant') this.loadGrants();
      if (t === 'package') this.loadPackages();
      if (t === 'admin') this.loadAdmins();
    },
    loadOverview() {
      this.$api.merchant.overview().then((res) => { this.overview = res.data || {}; });
    },
    loadCustomers() {
      this.$api.merchant.customer.page({
        page: this.custPage.page, size: this.custPage.size,
        keyword: this.keyword, status: this.statusFilter
      }).then((res) => {
        this.customers = (res.data && res.data.records) || [];
        this.custPage.total = (res.data && res.data.total) || 0;
      });
    },
    loadGrants() {
      this.$api.merchant.grant.page({
        page: this.grantPage.page, size: this.grantPage.size
      }).then((res) => {
        this.grants = (res.data && res.data.records) || [];
        this.grantPage.total = (res.data && res.data.total) || 0;
      });
    },
    loadPackages() {
      this.$api.merchant.package.page({ page: 1, size: 100 }).then((res) => {
        this.packages = (res.data && res.data.records) || [];
        this.pkgOptions = this.packages.filter((p) => p.status === '启用');
      });
    },
    loadAdmins() {
      this.$api.merchant.admin.list().then((res) => { this.admins = res.data || []; });
    },
    openCreate() {
      this.customerForm = { accountSetsNum: 1, accountingNum: 1, merchantStatus: '正常' };
      this.showCustomer = true;
    },
    openEdit(row) {
      this.customerForm = {
        id: row.id, customerName: row.customerName, realName: row.realName,
        email: row.email, accountSetsNum: row.accountSetsNum, accountingNum: row.accountingNum,
        merchantStatus: row.merchantStatus, expirationDate: row.expirationDate, packageId: row.packageId
      };
      this.showCustomer = true;
    },
    saveCustomer() {
      const d = Object.assign({}, this.customerForm);
      if (this.customerForm.id) {
        this.$api.merchant.customer.update(d).then(() => {
          this.$Message.success('已保存'); this.showCustomer = false; this.loadCustomers();
        });
      } else {
        if (!d.mobile) { this.$Message.error('手机号必填'); return; }
        this.$api.merchant.customer.save(d).then((res) => {
          const pwd = res.data && res.data.initPassword;
          this.$Message.success('客户已创建' + (pwd ? '，初始密码：' + pwd : ''));
          this.showCustomer = false; this.loadCustomers();
        });
      }
    },
    resetPwd(row) {
      this.$Confirm('确认重置该客户密码为随机6位？').then(() => {
        this.$api.merchant.customer.resetPassword(row.id).then((res) => {
          const pwd = res.data && res.data.password;
          this.$Message.success('新密码：' + pwd);
        });
      });
    },
    toggleStatus(row) {
      const next = row.merchantStatus === '停用' ? '正常' : '停用';
      this.$Confirm('确认将客户状态改为「' + next + '」？').then(() => {
        this.$api.merchant.customer.status({ id: row.id, status: next }).then(() => {
          this.$Message.success('已' + next); this.loadCustomers();
        });
      });
    },
    delCustomer(row) {
      this.$Confirm('确认删除该客户？若存在账套将被拒绝。').then(() => {
        this.$api.merchant.customer.delete(row.id).then(() => {
          this.$Message.success('已删除'); this.loadCustomers();
        });
      });
    },
    openAccountSet(row) {
      this.asCustomerId = row.id; this.asCustomerName = row.customerName || row.mobile;
      this.asForm = { accountingStandards: 0, vatType: 0 };
      this.showAccountSet = true;
    },
    submitAccountSet() {
      const d = Object.assign({ userId: this.asCustomerId }, this.asForm);
      this.$api.merchant.accountSets.open(d).then((res) => {
        this.$Message.success('账套「' + (res.data && res.data.companyName) + '」已开启');
        this.showAccountSet = false; this.loadCustomers();
      });
    },
    openGrant(row) {
      this.grantCustomerId = row.id; this.grantCustomerName = row.customerName || row.mobile;
      this.grantForm = { grantType: '开通', packageId: row.packageId || null };
      this.showGrant = true;
    },
    submitGrant() {
      const d = Object.assign({ userId: this.grantCustomerId }, this.grantForm);
      this.$api.merchant.grant.do(d).then(() => {
        this.$Message.success('已提交'); this.showGrant = false; this.loadCustomers(); this.loadOverview();
      });
    },
    openPackage(row) {
      if (row) {
        this.pkgForm = Object.assign({}, row);
      } else {
        this.pkgForm = {
          name: '', price: '', months: 12, accountSetsNum: 1, accountingNum: 1,
          cashierModule: 1, fixedAssetModule: 1, invoiceModule: 1, salaryModule: 1,
          reportModule: 1, status: '启用', remark: ''
        };
      }
      this.showPackage = true;
    },
    savePackage() {
      if (!this.pkgForm.name) { this.$Message.error('套餐名必填'); return; }
      this.$api.merchant.package.save(this.pkgForm).then(() => {
        this.$Message.success('已保存'); this.showPackage = false; this.loadPackages();
      });
    },
    delPackage(row) {
      this.$Confirm('确认删除该套餐？').then(() => {
        this.$api.merchant.package.delete(row.id).then(() => {
          this.$Message.success('已删除'); this.loadPackages();
        });
      });
    },
    saveAdmin() {
      if (!this.adminForm.mobile) { this.$Message.error('手机号必填'); return; }
      this.$api.merchant.admin.save(this.adminForm).then(() => {
        this.$Message.success('已添加'); this.showAdmin = false; this.loadAdmins();
      });
    },
    delAdmin(row) {
      this.$Confirm('确认删除该管理员？').then(() => {
        this.$api.merchant.admin.delete(row.id).then(() => {
          this.$Message.success('已删除'); this.loadAdmins();
        });
      });
    },
  }
};
</script>

<style scoped>
.merchant-page { padding: 16px; }
.denied { background:#fff; border-radius:4px; padding:32px; text-align:center; color:#666; }
.denied .tip { color:#999; font-size:13px; }
.cards { display:flex; gap:12px; flex-wrap:wrap; margin-bottom:16px; }
.card { background:#fff; border-radius:6px; padding:16px 20px; min-width:130px; box-shadow:0 1px 3px rgba(0,0,0,.06); }
.card .num { font-size:26px; font-weight:bold; }
.card .lbl { color:#888; font-size:13px; margin-top:4px; }
.card.warn .num { color:#e6a23c; }
.card.danger .num { color:#f56c6c; }
.tabs { display:flex; gap:8px; margin-bottom:12px; }
.tabs span { padding:8px 16px; background:#fff; border-radius:4px; cursor:pointer; font-size:14px; }
.tabs span.active { background:#348fe4; color:#fff; }
.panel { background:#fff; border-radius:4px; padding:16px; }
.bar { display:flex; gap:10px; align-items:center; margin-bottom:12px; flex-wrap:wrap; }
.search { height:30px; padding:0 8px; border:1px solid #ddd; border-radius:4px; width:200px; }
.empty { text-align:center; color:#aaa; padding:20px; }
.st-ok { color:#67c23a; }
.st-stop { color:#f56c6c; }
.mask { position:fixed; inset:0; background:rgba(0,0,0,.4); display:flex; align-items:center; justify-content:center; z-index:1000; }
.dialog { background:#fff; width:560px; max-width:92%; border-radius:6px; }
.dialog-head { padding:12px 16px; border-bottom:1px solid #eee; font-weight:bold; display:flex; justify-content:space-between; align-items:center; }
.dialog-head .x { cursor:pointer; font-size:20px; color:#999; }
.dialog-body { padding:16px; max-height:70vh; overflow:auto; }
.dialog-foot { padding:12px 16px; border-top:1px solid #eee; text-align:right; }
.frow { display:flex; align-items:center; margin-bottom:12px; }
.frow label { width:90px; color:#666; font-size:14px; }
.frow input, .frow select { flex:1; height:32px; padding:0 8px; border:1px solid #ddd; border-radius:4px; }
.frow .h-datepicker, .frow .h-input { flex:1; }
</style>
